import React from "react";
import { authFetch } from "../../HttpClient";
import { Icon, PrIco } from "./ProcUi";

// Assistant IA du manuel de procédures et de la base de connaissance — port de
// ProcAssistant / ProcAiMsg / ProcMascot (procedures-ai.jsx de la maquette).
// La maquette envoyait tout le contenu au modèle depuis le navigateur ; ici la
// question part à l'API (/api/assistant) qui recherche les passages utiles,
// applique les droits de lecture et renvoie la réponse avec ses sources.
// Les citations arrivent sous la forme [S1], [S2]… et deviennent des liens.

const URL_ASSISTANT = "https://backend-myalfa.vercel.app/api/assistant";

const lire = (cle) => {
  try {
    return JSON.parse(localStorage.getItem(cle)) || [];
  } catch (e) {
    return [];
  }
};

// Gras et citations dans une ligne de réponse.
const enLigne = (s, sources, onOpen) => {
  const parts = s.split(/(\*\*[^*]+\*\*|\[S\d+\](?:[\s,]*\[S\d+\])*)/g);
  return parts.map((x, i) => {
    if (/^\*\*.+\*\*$/.test(x)) return <strong key={i}>{x.slice(2, -2)}</strong>;
    if (/^\[S\d+\]/.test(x)) {
      return (
        <React.Fragment key={i}>
          {x.match(/S\d+/g).map((cle) => {
            const src = (sources || []).find((y) => y.cle === cle);
            return src ? (
              <button key={cle} className="pr-ai-cite" title={src.titre} onClick={() => onOpen(src)}>
                {src.titre}
              </button>
            ) : null;
          })}
        </React.Fragment>
      );
    }
    return <React.Fragment key={i}>{x}</React.Fragment>;
  });
};

export const ProcAiMsg = ({ text, sources, onOpen }) => {
  const blocks = [];
  text.split("\n").forEach((l) => {
    const t = l.trim();
    const ul = t.match(/^[-•*]\s+(.*)/);
    const ol = t.match(/^(\d+)[.)]\s+(.*)/);
    const last = blocks[blocks.length - 1];
    if (ul) {
      if (last && last.k === "ul") last.items.push(ul[1]);
      else blocks.push({ k: "ul", items: [ul[1]] });
    } else if (ol) {
      if (last && last.k === "ol") last.items.push(ol[2]);
      else blocks.push({ k: "ol", items: [ol[2]], start: +ol[1] });
    } else if (t) blocks.push({ k: "p", t: t.replace(/^#+\s*/, "") });
  });
  return (
    <div className="pr-ai-md">
      {blocks.map((b, i) =>
        b.k === "p" ? (
          <p key={i}>{enLigne(b.t, sources, onOpen)}</p>
        ) : b.k === "ul" ? (
          <ul key={i}>{b.items.map((x, j) => <li key={j}>{enLigne(x, sources, onOpen)}</li>)}</ul>
        ) : (
          <ol key={i} start={b.start}>{b.items.map((x, j) => <li key={j}>{enLigne(x, sources, onOpen)}</li>)}</ol>
        ),
      )}
    </div>
  );
};

// cfg : { corpus, cle (stockage local), titre, sousTitre, intro, sugg, note }
// contexte : { id, titre } de la page ou du dossier consulté (facultatif).
export const ProcAssistant = ({ cfg, contexte, onOpen, onClose, initialAsk }) => {
  const [msgs, setMsgs] = React.useState(() => lire(cfg.cle));
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const scRef = React.useRef(null);
  const taRef = React.useRef(null);
  const askedRef = React.useRef(null);

  React.useEffect(() => {
    try {
      localStorage.setItem(cfg.cle, JSON.stringify(msgs.slice(-30)));
    } catch (e) {
      // stockage indisponible : la conversation reste en mémoire
    }
  }, [msgs, cfg.cle]);
  React.useEffect(() => {
    if (scRef.current) scRef.current.scrollTop = scRef.current.scrollHeight;
  }, [msgs, busy]);
  React.useEffect(() => {
    if (taRef.current) taRef.current.focus();
  }, []);

  const ask = async (q) => {
    q = (q || "").trim();
    if (!q || busy) return;
    const avant = msgs;
    setMsgs([...avant, { role: "user", content: q, ctx: contexte ? contexte.id : null }]);
    setInput("");
    setBusy(true);
    setErr(null);
    try {
      const res = await authFetch(URL_ASSISTANT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          corpus: cfg.corpus,
          question: q,
          historique: avant.slice(-12).map((m) => ({ role: m.role, content: m.content })),
          contexte: contexte ? { id: contexte.id } : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const erreur = new Error(data.message || "Erreur " + res.status);
        // Quota IA du jour atteint (429) : le message du serveur est affiché tel quel.
        erreur.quota = !!data.quotaAtteint;
        throw erreur;
      }
      setMsgs((m) => [...m, { role: "assistant", content: (data.texte || "").trim() || "Je n'ai pas pu formuler de réponse.", sources: data.sources || [] }]);
    } catch (e) {
      console.error("Assistant IA :", e);
      setErr(e.quota ? e.message : "L'assistant n'a pas pu répondre. Réessayez dans un instant.");
      setMsgs(avant);
      setInput(q);
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => {
    if (initialAsk && initialAsk.n !== askedRef.current) {
      askedRef.current = initialAsk.n;
      ask(initialAsk.q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAsk && initialAsk.n]);

  return (
    <aside className="pr-ai">
      <div className="pr-ai-head">
        <span className="pr-ai-logo"><PrIco n="spark" s={16} /></span>
        <div className="pr-ai-ht">
          <b>{cfg.titre}</b>
          <small>{cfg.sousTitre}</small>
        </div>
        {msgs.length > 0 && (
          <button className="pr-ai-hb" title="Nouvelle conversation" onClick={() => { setMsgs([]); setErr(null); }}>
            <Icon name="plus" size={15} />
          </button>
        )}
        <button className="pr-ai-hb" title="Fermer" onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
      </div>
      <div className="pr-ai-scroll" ref={scRef}>
        {msgs.length === 0 && (
          <div className="pr-ai-empty">
            <p>{cfg.intro}</p>
            <div className="pr-ai-sugg">
              {cfg.sugg.map((s) => <button key={s} onClick={() => ask(s)}>{s}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="pr-ai-u">{m.content}</div>
          ) : (
            <div key={i} className="pr-ai-a"><ProcAiMsg text={m.content} sources={m.sources} onOpen={onOpen} /></div>
          ),
        )}
        {busy && <div className="pr-ai-a pr-ai-wait"><span></span><span></span><span></span></div>}
        {err && <div className="pr-ai-err"><Icon name="alert" size={14} /> {err}</div>}
      </div>
      <div className="pr-ai-foot">
        {contexte && <div className="pr-ai-ctx"><PrIco n="book" s={12} /> Contexte : {contexte.titre}</div>}
        <div className="pr-ai-in">
          <textarea
            ref={taRef}
            rows={1}
            value={input}
            placeholder="Votre question…"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                ask(input);
              }
            }}
          ></textarea>
          <button className="pr-ai-send" disabled={!input.trim() || busy} onClick={() => ask(input)} title="Envoyer">
            <PrIco n="send" s={15} />
          </button>
        </div>
        <div className="pr-ai-note">{cfg.note}</div>
      </div>
    </aside>
  );
};

export const ProcMascot = ({ onOpen, bubble }) => {
  const [idle, setIdle] = React.useState(false);
  const [look, setLook] = React.useState({ x: 0, y: 0 });
  const ref = React.useRef(null);
  React.useEffect(() => {
    let t;
    let raf;
    const arm = () => {
      clearTimeout(t);
      setIdle(false);
      t = setTimeout(() => setIdle(true), 7000);
    };
    const move = (e) => {
      arm();
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        const d = Math.hypot(dx, dy) || 1;
        setLook({ x: (dx / d) * 2.5, y: (dy / d) * 2 });
      });
    };
    const evs = ["keydown", "scroll", "mousedown", "wheel"];
    evs.forEach((e) => window.addEventListener(e, arm, true));
    window.addEventListener("mousemove", move);
    arm();
    return () => {
      clearTimeout(t);
      if (raf) cancelAnimationFrame(raf);
      evs.forEach((e) => window.removeEventListener(e, arm, true));
      window.removeEventListener("mousemove", move);
    };
  }, []);
  return (
    <div className={`pr-masc ${idle ? "idle" : ""}`}>
      <div className="pr-masc-bubble" onClick={onOpen}>{bubble || "Une question sur une procédure ? Je peux vous aider."}</div>
      <button ref={ref} className="pr-masc-bot" onClick={onOpen} title="Demander à l'assistant" aria-label="Ouvrir l'assistant IA">
        <span className="pr-masc-ant"><span></span></span>
        <span className="pr-masc-head">
          <span className="pr-masc-face" style={{ transform: `translate(${look.x}px, ${look.y}px)` }}>
            <span className="pr-masc-eye"></span>
            <span className="pr-masc-eye"></span>
            <span className="pr-masc-mouth"></span>
          </span>
        </span>
        <span className="pr-masc-hand"></span>
      </button>
    </div>
  );
};
